import { cropImage } from '~/shared/utils/image'
import { useRegions } from '~/composables/regions/use-regions'
import { useExternalHost } from '~/composables/use-external-host'

function toApiPath(path: string): string {
  if (!path || path === '/') return 'main'

  const clean = path.replace(/^\/+|\/+$/g, '')
  const segments = clean.split('/')
  const slug = segments[1]

  if (segments.length === 2 && segments[0] === 'vacancies' && slug?.startsWith('region-')) {
    return `vacancies/${slug.slice('region-'.length)}`
  }

  return clean
}

export function useSeoMetadata() {
  const route = useRoute()
  const { $services } = useNuxtApp()
  const externalHost = useExternalHost()
  const { selectedRegion, loadRegions } = useRegions()

  const isVacanciesBase = computed(() => route.path.replace(/\/+$/, '') === '/vacancies')

  const { data: seoData } = useAsyncData(
    'seo-metadata',
    async () => {
      if (route.name?.toString().startsWith('account')) return null

      try {
        let apiPath = toApiPath(route.path)

        if (apiPath === 'vacancies') {
          await loadRegions()
          apiPath = selectedRegion.value?.slug
            ? `vacancies/${selectedRegion.value.slug}`
            : 'vacancies/region'
        }

        return await $services.pageSeoService.getByUrl(apiPath)
      }
      catch {
        return null
      }
    },
    { watch: [() => route.fullPath, () => (isVacanciesBase.value ? selectedRegion.value?.slug : undefined)] },
  )

  useHead(() => {
    const page = seoData.value
    if (!page || page.isEmpty) return {}

    const pageUrl = externalHost + route.fullPath
    const title = page.seo_title || page.title
    const description = page.seo_description || undefined
    const ogImage = page.image ? externalHost + cropImage(page.image) : undefined
    const keywords = page.seo_keywords || undefined

    let canonicalHref: string | undefined
    const canonicalUrl = page.canonical_link || `/${page.path}`
    try {
      const canonical = new URL(canonicalUrl, externalHost)
      canonical.search = ''
      canonical.hash = ''
      canonicalHref = canonical.toString()
    }
    catch {
      console.warn('[useSeoMetadata] Invalid canonical URL:', canonicalUrl)
    }

    return {
      title,
      meta: [
        ...(description ? [{ name: 'description', content: description }] : []),
        ...(keywords ? [{ name: 'keywords', content: keywords }] : []),
        ...(title ? [{ property: 'og:title', content: title }] : []),
        ...(description ? [{ property: 'og:description', content: description }] : []),
        { property: 'og:type', content: page.page_type === 'news' ? 'article' : 'website' },
        { property: 'og:url', content: pageUrl },
        ...(ogImage ? [{ property: 'og:image', content: ogImage }] : []),
        ...(title ? [{ name: 'twitter:title', content: title }] : []),
        ...(description ? [{ name: 'twitter:description', content: description }] : []),
        { name: 'twitter:card', content: ogImage ? 'summary_large_image' : 'summary' },
        ...(ogImage ? [{ name: 'twitter:image', content: ogImage }] : []),
      ],
      link: [
        ...(canonicalHref ? [{ rel: 'canonical' as const, href: canonicalHref }] : []),
      ],
    }
  })

  return { seoData }
}
